import { ArrowLeftMedium, ArrowRightMedium } from '@zoopla-eng/zds/icons';
import { ButtonLink, IconButton } from '@zoopla-eng/zds/button';
import { PageSubtitleShort } from '@zoopla-eng/zds/headings';
import { FC, SyntheticEvent, useState } from 'react';
import { defaultForSaleAreas } from './defaultForSaleAreas';
import { defaultToRentAreas } from './defaultToRentAreas';
import {
    contentContainerStyle,
    desktopLinkStyle,
    headingStyle,
    mobileLinkStyle,
    searchSuggestionsTownsAndCitiesWrapperStyle,
    styledArrowsWrapperMobileStyle,
    styledArrowsWrapperTabletStyle,
    tabButtonsStyle,
    tabButtonStyle,
    tabContentStyle,
    townsAndCitiesHeadlineWrapperStyle
} from './SearchSuggestionsTownsAndCities.styles.css';
import {
    trackBrowseAllCitiesTownsUK,
    trackIndividualLocationForSale,
    trackIndividualLocationToRent,
    trackSeeAllUKForSale,
    trackSeeAllUKToRent
} from './SearchSuggestionsTownsAndCities.tracking';
import { SearchSuggestionsTownsAndCitiesProps } from './SearchSuggestionsTownsAndCities.types';
import { SearchSuggestionTownsAndCityLinks } from './SearchSuggestionTownsAndCitiesLinks';
import { fontGroups } from '@zoopla-eng/zds/themes';

const MAIN_SITE_LINK = 'https://www.zoopla.co.uk';
const sitemapLink = `${MAIN_SITE_LINK}/sitemap/`;

enum Tab {
    ForSale = 'for-sale-tab',
    ToRent = 'to-rent-tab'
}

const getCarouselMargin = (carouselIndex: number) => {
    switch (carouselIndex) {
        case 0:
            return 0;
        case 1:
            return '-100%';
        case 2:
            return '-200%';
        case 3:
            return '-300%';
        default:
            return 0;
    }
};

export const SearchSuggestionsTownsAndCities: FC<
    React.PropsWithChildren<SearchSuggestionsTownsAndCitiesProps>
> = ({
    forSaleAreas = defaultForSaleAreas,
    toRentAreas = defaultToRentAreas
}) => {
    const [selectedTabId, setSelectedTabId] = useState(Tab.ForSale);
    const [carouselIndex, setCarouselIndex] = useState(0);

    const isShowingForSale = selectedTabId === Tab.ForSale;
    const isShowingToRent = selectedTabId === Tab.ToRent;

    const decrementCarouselIndex = () => {
        setCarouselIndex((previousIndex) => previousIndex - 1);
    };

    const incrementCarouselIndex = () => {
        setCarouselIndex((previousIndex) => previousIndex + 1);
    };

    return (
        <div className={searchSuggestionsTownsAndCitiesWrapperStyle}>
            <div className={townsAndCitiesHeadlineWrapperStyle}>
                <div className={`${headingStyle} ${fontGroups.kettle}`}>
                    <PageSubtitleShort>
                        Discover towns and cities
                    </PageSubtitleShort>
                </div>
            </div>
            <div className={contentContainerStyle}>
                <div className={tabButtonsStyle} role="tablist">
                    <a
                        className={`${tabButtonStyle({
                            active: isShowingForSale
                        })} ${fontGroups.ladle}`}
                        href="/for-sale/"
                        role="tab"
                        aria-selected={isShowingForSale}
                        aria-controls="suggestions-content-for-sale"
                        id="suggestions-tab-for-sale"
                        onClick={(event: SyntheticEvent) => {
                            event.preventDefault();
                            setSelectedTabId(Tab.ForSale);
                        }}
                        data-testid="for-sale-tab"
                    >
                        For sale
                    </a>
                    <a
                        className={`${tabButtonStyle({
                            active: isShowingToRent
                        })} ${fontGroups.ladle}`}
                        role="tab"
                        aria-selected={isShowingToRent}
                        aria-controls="suggestions-content-to-rent"
                        id="suggestions-tab-to-rent"
                        data-testid="to-rent-tab"
                        href="/to-rent/"
                        onClick={(event: SyntheticEvent) => {
                            event.preventDefault();
                            setSelectedTabId(Tab.ToRent);
                        }}
                    >
                        To rent
                    </a>
                </div>

                <div className={desktopLinkStyle}>
                    <ButtonLink
                        href={sitemapLink}
                        category="tertiary"
                        data-testid="explore_more-cta-desktop"
                        onClick={() => trackBrowseAllCitiesTownsUK}
                    >
                        Explore more towns &amp; cities
                    </ButtonLink>
                </div>

                <div className={styledArrowsWrapperMobileStyle}>
                    <IconButton
                        disabled={carouselIndex === 0}
                        label="Arrow left"
                        category="tertiary"
                        data-testid={`arrow_full_left_mobile`}
                        onClick={decrementCarouselIndex}
                    >
                        {ArrowLeftMedium}
                    </IconButton>
                    <IconButton
                        disabled={carouselIndex === 3}
                        label="Arrow right"
                        category="tertiary"
                        data-testid={`arrow_full_right_mobile`}
                        onClick={incrementCarouselIndex}
                    >
                        {ArrowRightMedium}
                    </IconButton>
                </div>

                <div className={styledArrowsWrapperTabletStyle}>
                    <IconButton
                        disabled={carouselIndex === 0}
                        label="Arrow left"
                        category="tertiary"
                        data-testid={`arrow_full_left_tablet`}
                        onClick={decrementCarouselIndex}
                    >
                        {ArrowLeftMedium}
                    </IconButton>
                    <IconButton
                        disabled={carouselIndex === 1}
                        label="Arrow right"
                        category="tertiary"
                        data-testid={`arrow_full_right_tablet`}
                        onClick={incrementCarouselIndex}
                    >
                        {ArrowRightMedium}
                    </IconButton>
                </div>

                <div
                    className={tabContentStyle({
                        hidden: !isShowingForSale
                    })}
                    role="tabpanel"
                    aria-labelledby="suggestions-tab-for-sale"
                    id="suggestions-content-for-sale"
                    style={{
                        marginLeft: getCarouselMargin(carouselIndex)
                    }}
                >
                    <SearchSuggestionTownsAndCityLinks
                        areas={forSaleAreas}
                        trackSeeAll={trackSeeAllUKForSale}
                        trackSeeIndividual={trackIndividualLocationForSale}
                    />
                </div>

                <div
                    className={tabContentStyle({
                        hidden: !isShowingToRent
                    })}
                    role="tabpanel"
                    aria-labelledby="suggestions-tab-to-rent"
                    id="suggestions-content-to-rent"
                    style={{
                        marginLeft: getCarouselMargin(carouselIndex)
                    }}
                >
                    <SearchSuggestionTownsAndCityLinks
                        areas={toRentAreas}
                        trackSeeAll={trackSeeAllUKToRent}
                        trackSeeIndividual={trackIndividualLocationToRent}
                    />
                </div>
            </div>
            <div className={mobileLinkStyle}>
                <ButtonLink
                    href={sitemapLink}
                    category="tertiary"
                    data-testid="explore_more-cta-mobile"
                    onClick={() => trackBrowseAllCitiesTownsUK()}
                >
                    Explore more towns &amp; cities
                </ButtonLink>
            </div>
        </div>
    );
};
